import { useState } from 'react';
import { useLogin } from '@/main/auth/hooks/useLogin.hook';
import { sqAuthApi } from '@seeqdev/api/endpoint/AuthApi';
import { sqUsersApi } from '@seeqdev/api/endpoint/UsersApi';
import type { AxiosError } from 'axios';
import type { SeeqErrorData } from '@seeqdev/api';
import type { DatasourceOutputV1 } from '@seeqdev/api/model/DatasourceOutputV1';

interface FormData {
  firstName: string;
  lastName: string;
  username: string;
  password: string;
  passwordCheck: string;
  [key: string]: string | undefined;
}

export const useAuth = ({ seeqDomain, domain }: { seeqDomain: DatasourceOutputV1; domain: DatasourceOutputV1 }) => {
  const { doLogin } = useLogin({ domain });

  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    username: '',
    password: '',
    passwordCheck: '',
  });
  const [serverMessage, setServerMessage] = useState('');
  const [forgotPasswordError, setForgotPasswordError] = useState('');

  const { firstName, lastName, username, password } = formData;

  const updateFormData = (data: Partial<typeof formData>) =>
    setFormData((formData) => ({
      ...formData,
      ...data,
    }));

  /**
   * Submits a request to generate a new user and executes a consecutive login.
   *
   * @returns {Promise} that resolves after the user was created and logged in
   */
  const register = () => {
    setServerMessage('');

    return sqUsersApi
      .createUser({
        firstName,
        lastName,
        username,
        email: username,
        password,
      })
      .then(() => doLogin({ username, password }, seeqDomain.datasourceClass!, seeqDomain.datasourceId!))
      .catch((error: AxiosError<SeeqErrorData>) => {
        const statusMessage = error?.response?.data?.statusMessage;
        if (statusMessage) {
          setServerMessage(`(${statusMessage})`);
        }
      });
  };

  const requestPasswordReset = () => {
    setServerMessage('');

    return sqAuthApi
      .requestPasswordReset({
        email: username,
      })
      .catch((error: AxiosError<SeeqErrorData>) => {
        const statusMessage = error?.response?.data?.statusMessage;
        if (statusMessage) {
          setForgotPasswordError(statusMessage);
        }
      });
  };

  return { updateFormData, formData, register, serverMessage, requestPasswordReset, forgotPasswordError };
};
